/**
 * The geometry of the calls-per-day chart (`<CallsChart>`), shared by the home
 * page and the pages of statistics.
 */

import type { McpUsageDay } from "@/lib/api";

/** Share of its band a column fills, per the figure style (about 65%). */
const COLUMN_SHARE = 0.65;

/**
 * What the chart shows: every call, the failed ones on top of the successful
 * ones (`all`), or the failed calls alone (`errors`).
 */
export type ChartSeries = "all" | "errors";

/**
 * How a chart of failures counts them: as a number, or as a percentage of
 * that day's calls (`<MeasureSwitch>`).
 */
export type ChartMeasure = "count" | "percent";

/**
 * One day's column in the calls chart. Every coordinate is a percentage of the
 * plot area (x from the left, y from the top), so the chart sets SVG
 * attributes rather than inline styles.
 */
export interface ChartColumn {
  day: string;
  ok: number;
  errors: number;
  total: number;
  /** The share of the day's calls that failed; null on a day without calls. */
  share: number | null;
  /**
   * What the column stands for, and its label: the calls, the failures, or
   * their share. Null on a day without a column (no calls, as a percentage).
   */
  value: number | null;
  /** The column's left edge and width, centred in its band. */
  x: number;
  width: number;
  /** The band's centre, for labels. */
  centre: number;
  /** The column's top; 100 for a day without a column. */
  top: number;
  /**
   * Where the successful calls at the base end and the failures on top start;
   * 100 for the failures alone, which are all column.
   */
  split: number;
}

/** A chart's settings: what it shows and how it counts failures. */
export interface ChartOptions {
  series?: ChartSeries;
  /** Only for the failures alone: a percentage is always of that day's calls. */
  measure?: ChartMeasure;
}

/**
 * The chart's columns, scaled to the day with the most (at least 1, so empty
 * days stay flat; a percentage to the highest share). `max` is the value axis'
 * top: a count, or a share for a percentage.
 */
export function chartColumns(
  data: readonly McpUsageDay[],
  { series = "all", measure = "count" }: ChartOptions = {},
): { columns: ChartColumn[]; max: number } {
  const percent = series === "errors" && measure === "percent";
  const valueOf = (d: McpUsageDay): number | null => {
    const total = d.ok + d.errors;
    if (series === "all") {
      return total;
    }
    if (!percent) {
      return d.errors;
    }
    return total > 0 ? d.errors / total : null;
  };
  const values = data.map(valueOf).filter((v): v is number => v !== null);
  const highest = Math.max(0, ...values);
  const max = percent ? (highest > 0 ? highest : 1) : Math.max(1, highest);
  const band = data.length > 0 ? 100 / data.length : 100;
  const height = (v: number) => (v / max) * 100;
  const columns = data.map((d, index) => {
    const total = d.ok + d.errors;
    const value = valueOf(d);
    const top = value === null ? 100 : 100 - height(value);
    return {
      day: d.day,
      ok: d.ok,
      errors: d.errors,
      total,
      share: total > 0 ? d.errors / total : null,
      value,
      x: (index + (1 - COLUMN_SHARE) / 2) * band,
      width: COLUMN_SHARE * band,
      centre: (index + 0.5) * band,
      top,
      // A column of failures alone has no successful calls under it.
      split: series === "all" ? 100 - height(d.ok) : 100,
    };
  });
  return { columns, max };
}
